/**
 * ImportOptionsModal
 *
 * The "Choose destination" modal opened from the ReviewImportStep summary
 * phase. It lets the user pick where the imported contacts land and (optionally)
 * which tags to apply, then hands everything to the caller's import handler.
 *
 * Sections:
 *   - Destination: two radio cards — "Import to CRM" (default) and
 *     "Import to a campaign" (also imports to CRM).
 *   - Campaign picker (campaign destination only): a DS2026 searchable dropdown
 *     (click to open, type to filter) listing real campaigns with a color dot +
 *     workflow-status badge; picking one is single-select.
 *   - Add tags (optional): a DS2026 searchable multi-select dropdown; chosen
 *     tags render as removable chips below it.
 *
 * Both pickers are Popover overlays (cmdk-backed) so opening them never resizes
 * the modal.
 *
 * Real data: campaigns via `api.useGetCampaigns()`, tags via
 * `api.useGetTags('contacts')`.
 */
import { useState } from 'react'
import {
  PiCaretDown,
  PiCaretRight,
  PiCheck,
  PiCircleNotch,
  PiMegaphone,
  PiPlus,
  PiX,
} from 'react-icons/pi'
import { Alert } from 'components/FbUI/Alert'
import { cn } from 'components/UI/cn'
import { FbModal } from 'components/UI/Modal'
import { Popover, PopoverContent, PopoverTrigger } from 'components/UI/popover'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from 'components/UI/command'
import useApiService from 'services/api'
import { useGetCampaignById } from 'features/campaigns/campaigns_api'
import { ContactTag } from 'models/tags'
import {
  Campaign,
  WORKFLOW_STATUS,
  WORKFLOW_STATUS_LABELS,
} from 'models/campaign'
import { radioGroupKeyDown } from './radioGroupKeyDown'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ImportDestination = 'crm' | 'campaign'

export interface ImportOptions {
  destination: ImportDestination
  campaignId: number | null
  campaignName: string | null
  /** Tag names selected in the modal (existing + newly typed). */
  tagNames: string[]
}

export interface ImportOptionsModalProps {
  onClose: () => void
  onConfirm: (opts: ImportOptions) => void
  /** When set, locks the destination to 'campaign' and this campaign ID. */
  lockedCampaignId?: number | null
  /**
   * True when the Map step mapped one or more columns to custom campaign
   * columns. Those are campaign-scoped, so the import must go to a campaign —
   * the "Import to CRM" option is disabled and a note is shown.
   */
  hasCustomColumns?: boolean
}

// ---------------------------------------------------------------------------
// Shared DS2026 dropdown styling (matches MappingSelect on Step 2)
// ---------------------------------------------------------------------------

const TRIGGER_CLASS =
  'flex w-full items-center justify-between gap-2 rounded-lg border border-line-form bg-surface py-2.5 pl-3 pr-2.5 text-sm transition-colors hover:bg-surface-sunken focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/30 data-[state=open]:border-brand-active'

const CONTENT_CLASS =
  'w-[var(--radix-popover-trigger-width)] overflow-hidden rounded-xl border border-neutral-200 bg-surface p-0 shadow-[0px_4px_8px_-2px_rgba(16,24,40,0.10),0px_2px_4px_-2px_rgba(16,24,40,0.06)]'

const ITEM_CLASS =
  '!cursor-pointer !gap-2 !rounded-lg !px-3 !py-2 !text-sm !font-medium !text-neutral-700 data-[selected=true]:!bg-neutral-100 data-[selected=true]:!text-neutral-900'

// Campaign workflow-status badge colors, via contrast-checked semantic tokens
// (same status families the app uses, but AA + token-based instead of raw
// palette classes). Unknown statuses fall back to neutral.
const STATUS_BADGE_CLASS: Record<string, string> = {
  [WORKFLOW_STATUS.DRAFT]: 'bg-surface-sunken text-fg-secondary',
  [WORKFLOW_STATUS.CONTACTS_REQUESTED]: 'bg-info-bg-subtle text-info-strong',
  [WORKFLOW_STATUS.READY_TO_LAUNCH]: 'bg-info-bg-subtle text-info-strong',
  [WORKFLOW_STATUS.SCHEDULED]: 'bg-info-bg-subtle text-info-strong',
  [WORKFLOW_STATUS.ACTIVE]: 'bg-success-bg-subtle text-success-strong',
  [WORKFLOW_STATUS.COMPLETE]: 'bg-success-bg-subtle text-success-strong',
  [WORKFLOW_STATUS.PAUSED]: 'bg-warning-bg-subtle text-warning-strong',
}

// ---------------------------------------------------------------------------
// Campaign picker — single-select searchable dropdown
// ---------------------------------------------------------------------------

function CampaignCombobox({
  campaigns,
  isLoading,
  selected,
  onSelect,
}: {
  campaigns: Campaign[]
  isLoading: boolean
  selected: Campaign | null
  onSelect: (campaign: Campaign | null) => void
}) {
  const [open, setOpen] = useState(false)
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Select campaign"
          aria-expanded={open}
          className={TRIGGER_CLASS}
        >
          {selected ? (
            <span className="flex min-w-0 items-center gap-2">
              <CampaignDot color={selected.color} />
              <span className="truncate text-fg">{selected.name}</span>
            </span>
          ) : (
            <span className="text-fg-secondary">Select a campaign…</span>
          )}
          <PiCaretDown className="size-4 shrink-0 text-fg-tertiary" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className={CONTENT_CLASS}>
        <Command>
          <CommandInput placeholder="Search campaigns…" className="text-sm" />
          <CommandList className="max-h-64">
            {isLoading ? (
              <div className="flex items-center gap-2 px-3 py-4 text-sm text-fg-secondary">
                <PiCircleNotch className="size-4 shrink-0 animate-spin" />
                Loading campaigns…
              </div>
            ) : (
              <>
                <CommandEmpty className="px-3 py-4 text-center text-sm text-fg-secondary">
                  No campaigns found
                </CommandEmpty>
                <CommandGroup className="p-1.5">
                  {campaigns.map((c) => {
                    const isSel = selected?.id === c.id
                    const statusLabel = c.workflow_status
                      ? WORKFLOW_STATUS_LABELS[c.workflow_status]
                      : undefined
                    return (
                      <CommandItem
                        key={c.id}
                        value={`${c.name} ${c.id}`}
                        className={ITEM_CLASS}
                        onSelect={() => {
                          onSelect(isSel ? null : c)
                          setOpen(false)
                        }}
                      >
                        <CampaignDot color={c.color} />
                        <span className="min-w-0 flex-1 truncate">
                          {c.name}
                        </span>
                        {statusLabel && (
                          <span
                            className={cn(
                              'shrink-0 rounded-full px-1.5 py-0.5 text-xs font-medium',
                              STATUS_BADGE_CLASS[c.workflow_status] ??
                                'bg-surface-sunken text-fg-secondary'
                            )}
                          >
                            {statusLabel}
                          </span>
                        )}
                        <PiCheck
                          className={cn(
                            'size-4 shrink-0 text-brand-active',
                            !isSel && 'opacity-0'
                          )}
                        />
                      </CommandItem>
                    )
                  })}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

/** Campaign color dot — always a dot (neutral gray when no color is set). */
function CampaignDot({ color }: { color?: string | null }) {
  return (
    <span
      className={cn('size-2 shrink-0 rounded-full', !color && 'bg-fg-tertiary')}
      style={color ? { backgroundColor: color } : undefined}
    />
  )
}

// ---------------------------------------------------------------------------
// Tags picker — multi-select searchable dropdown + chips
// ---------------------------------------------------------------------------

function TagsCombobox({
  tags,
  selected,
  onToggle,
}: {
  tags: ContactTag[]
  selected: Set<string>
  onToggle: (name: string) => void
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')

  // Selected chips, including names typed-and-created here that aren't (yet) in
  // the fetched tag list — the import engine creates them by name on import.
  const selectedList: { tag: string; color?: string | null }[] = Array.from(
    selected
  ).map(
    (name) => tags.find((t) => t.tag === name) ?? { tag: name, color: null }
  )

  const trimmedQuery = query.trim()
  const queryMatchesExisting = tags.some(
    (t) => t.tag.toLowerCase() === trimmedQuery.toLowerCase()
  )
  const queryAlreadySelected = Array.from(selected).some(
    (name) => name.toLowerCase() === trimmedQuery.toLowerCase()
  )
  // Offer "Create" only when the search is non-empty and matches no existing or
  // already-selected tag. Mirror the 50-char tag limit enforced server-side.
  const canCreate =
    trimmedQuery.length > 0 &&
    trimmedQuery.length <= 50 &&
    !queryMatchesExisting &&
    !queryAlreadySelected

  const handleCreate = () => {
    if (!canCreate) return
    onToggle(trimmedQuery)
    setQuery('')
  }

  return (
    <div className="flex flex-col gap-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label="Add tags"
            aria-expanded={open}
            className={TRIGGER_CLASS}
          >
            <span
              className={cn(
                'truncate',
                selected.size === 0 && 'text-fg-secondary'
              )}
            >
              {selected.size === 0
                ? 'Select tags…'
                : `${selected.size} tag${selected.size !== 1 ? 's' : ''} selected`}
            </span>
            <PiCaretDown className="size-4 shrink-0 text-fg-tertiary" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className={CONTENT_CLASS}>
          <Command>
            <CommandInput
              value={query}
              onValueChange={setQuery}
              placeholder="Search or create a tag…"
              className="text-sm"
            />
            <CommandList className="max-h-64">
              <CommandEmpty className="p-1.5">
                {canCreate ? (
                  <button
                    type="button"
                    onClick={handleCreate}
                    className="flex w-full items-center gap-2 rounded-lg px-3 py-1.5 text-left text-sm font-medium text-brand-active transition-colors hover:bg-neutral-100"
                  >
                    <PiPlus className="size-4 shrink-0" />
                    <span className="min-w-0 flex-1 truncate">
                      Create &ldquo;{trimmedQuery}&rdquo;
                    </span>
                  </button>
                ) : (
                  <p className="px-3 py-2.5 text-center text-sm text-fg-secondary">
                    No tags found
                  </p>
                )}
              </CommandEmpty>
              <CommandGroup className="p-1.5">
                {tags.map((t) => {
                  const isSel = selected.has(t.tag)
                  return (
                    <CommandItem
                      key={t.tag}
                      value={t.tag}
                      className={ITEM_CLASS}
                      // Multi-select: toggle without closing the popover.
                      onSelect={() => onToggle(t.tag)}
                    >
                      <TagDot color={t.color} />
                      <span className="min-w-0 flex-1 truncate">{t.tag}</span>
                      <PiCheck
                        className={cn(
                          'size-4 shrink-0 text-brand-active',
                          !isSel && 'opacity-0'
                        )}
                      />
                    </CommandItem>
                  )
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {selectedList.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selectedList.map((t) => (
            <span
              key={t.tag}
              className="inline-flex items-center gap-1.5 rounded-full border border-line bg-surface-sunken px-2.5 py-1 text-xs font-medium text-fg"
            >
              <TagDot color={t.color} />
              {t.tag}
              <button
                type="button"
                onClick={() => onToggle(t.tag)}
                aria-label={`Remove ${t.tag}`}
                className="text-fg-tertiary transition-colors hover:text-fg"
              >
                <PiX className="size-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

/** Tag color dot — shown only when the tag actually has a color, so the dot
 *  always reflects the real tag color (no uniform-gray placeholder). */
function TagDot({ color }: { color?: string | null }) {
  if (!color) return null
  return (
    <span
      className="size-1.5 shrink-0 rounded-full"
      style={{ backgroundColor: color }}
    />
  )
}

// ---------------------------------------------------------------------------
// Destination radio options
// ---------------------------------------------------------------------------

const DESTINATION_OPTIONS: {
  value: ImportDestination
  label: string
  description: string
}[] = [
  {
    value: 'crm',
    label: 'Import to CRM',
    description: 'Add contacts to First Bite CRM only',
  },
  {
    value: 'campaign',
    label: 'Import to a campaign',
    description: 'Add to a campaign — also imports to CRM',
  },
]

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Modal for choosing the import destination (CRM vs campaign) plus optional
 * tags. On confirm, calls `onConfirm` with the chosen destination, campaign,
 * and tag names; the caller closes the modal and runs the import.
 */
export function ImportOptionsModal({
  onClose,
  onConfirm,
  lockedCampaignId = null,
  hasCustomColumns = false,
}: ImportOptionsModalProps) {
  const api = useApiService()
  const isLocked = lockedCampaignId != null
  // Custom campaign columns are campaign-scoped, so the import must target a
  // campaign (CRM-only would silently drop them). Force the campaign
  // destination and disable the CRM option when any were mapped.
  const mustUseCampaign = isLocked || hasCustomColumns
  // Only fetch the full campaigns list when NOT in locked mode. In locked mode
  // the picker is hidden; useGetCampaignById (below) fetches the single campaign.
  const { data: campaigns, isLoading: isCampaignsLoading } =
    api.useGetCampaigns(undefined, isLocked)
  const { data: contactTags = [] } = api.useGetTags('contacts')

  // Fetch only the single campaign by id when locked (avoids a full-list
  // fetch that may not include campaigns past page 1 in large workspaces).
  // useGetCampaignById auto-disables when lockedCampaignId is null/0.
  const { data: lockedCampaign = null, isError: isLockedCampaignError } =
    useGetCampaignById(lockedCampaignId)

  // When locked or custom columns are present, destination must be 'campaign';
  // otherwise start at 'crm'.
  const [destination, setDestination] = useState<ImportDestination>(
    mustUseCampaign ? 'campaign' : 'crm'
  )
  // selectedCampaign is only used in free (unlocked) mode.
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(
    null
  )
  const [selectedTags, setSelectedTags] = useState<Set<string>>(new Set())

  // The effective campaign for display/submission.
  const effectiveCampaign = isLocked ? lockedCampaign : selectedCampaign

  const needsCampaign = destination === 'campaign'
  // In locked mode the campaign is fixed, but wait for the single-campaign
  // fetch to resolve before allowing import — otherwise confirming early sends
  // campaignName=null and the success screen shows a blank campaign name.
  // Unblock on fetch error too, so a failed/deleted-campaign lookup can't
  // permanently disable the button: the import still carries the correct
  // campaignId (from the URL) and the backend validates it.
  const canImport = isLocked
    ? lockedCampaign !== null || isLockedCampaignError
    : !needsCampaign || selectedCampaign !== null

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const toggleTag = (name: string) =>
    setSelectedTags((prev) => {
      const next = new Set(prev)
      if (next.has(name)) {
        next.delete(name)
      } else {
        next.add(name)
      }
      return next
    })

  const handleConfirm = () => {
    if (!canImport) return
    if (isLocked) {
      onConfirm({
        destination: 'campaign',
        campaignId: lockedCampaignId,
        campaignName: lockedCampaign?.name ?? null,
        tagNames: Array.from(selectedTags),
      })
      return
    }
    onConfirm({
      destination,
      campaignId: needsCampaign ? (selectedCampaign?.id ?? null) : null,
      campaignName: needsCampaign ? (selectedCampaign?.name ?? null) : null,
      tagNames: Array.from(selectedTags),
    })
  }

  return (
    <FbModal
      open
      onOpenChange={(open: boolean) => {
        if (!open) onClose()
      }}
      title="Import options"
      description="Choose where to import these contacts"
      noPadding
      // Drop the default `mt-3` on the body wrapper so the content sits closer
      // to the header — scoped to this modal, not FbModal globally.
      childrenContainerClassName="flex-1 min-h-0 w-full max-w-full overflow-x-hidden ds-modal-scroll"
      blockAccept={!canImport}
      onAccept={handleConfirm}
      acceptButtonText=""
      acceptButtonProps={{
        children: (
          <>
            Start import
            <PiCaretRight className="size-4" />
          </>
        ),
      }}
      onCancel={onClose}
      cancelButtonText="Cancel"
    >
      <div className="flex flex-col gap-4 px-5 pb-5 pt-6">
        {/* ── Custom-column notice ── */}
        {hasCustomColumns && (
          <Alert
            color="info"
            icon={PiMegaphone}
            description="Custom campaign columns added — this import must go to a campaign."
          />
        )}

        {/* ── Destination ── */}
        <div className="flex flex-col gap-2">
          <p className="text-xs font-semibold text-fg-tertiary uppercase tracking-wide">
            Destination
          </p>
          {isLocked ? (
            /* Locked: show a single read-only destination row */
            <div className="flex items-start gap-3 px-4 py-3 rounded-xl border-2 border-brand-active bg-basil-50">
              <span className="size-4 rounded-full border-2 border-brand-active bg-brand-active flex items-center justify-center shrink-0 mt-0.5">
                <span className="size-1.5 rounded-full bg-white" />
              </span>
              <span className="flex flex-col">
                <span className="text-sm font-semibold text-brand-active">
                  Import to a campaign
                </span>
                <span className="text-xs text-fg-secondary mt-0.5">
                  Add to a campaign — also imports to CRM
                </span>
              </span>
            </div>
          ) : (
            <div
              role="radiogroup"
              aria-label="Import destination"
              className="flex flex-col gap-2"
            >
              {DESTINATION_OPTIONS.map((opt) => {
                const checked = destination === opt.value
                // Custom columns require a campaign, so the CRM option is
                // disabled (it would silently drop the column values).
                const optionDisabled = opt.value === 'crm' && mustUseCampaign
                return (
                  <button
                    key={opt.value}
                    type="button"
                    role="radio"
                    aria-checked={checked}
                    tabIndex={checked ? 0 : -1}
                    disabled={optionDisabled}
                    onClick={() => {
                      if (!optionDisabled) setDestination(opt.value)
                    }}
                    onKeyDown={(e) =>
                      radioGroupKeyDown(
                        e,
                        DESTINATION_OPTIONS.map((o) => ({
                          value: o.value,
                          disabled: o.value === 'crm' && mustUseCampaign,
                        })),
                        destination,
                        setDestination
                      )
                    }
                    className={cn(
                      'flex items-start gap-3 px-4 py-3 rounded-xl border-2 text-left transition-colors',
                      checked
                        ? 'border-brand-active bg-basil-50'
                        : 'border-line hover:border-line-form',
                      optionDisabled && 'opacity-40 cursor-not-allowed'
                    )}
                  >
                    <span
                      className={cn(
                        'size-4 rounded-full border-2 flex items-center justify-center shrink-0 mt-0.5 transition-colors',
                        checked
                          ? 'border-brand-active bg-brand-active'
                          : 'border-line-form bg-surface'
                      )}
                    >
                      {checked && (
                        <span className="size-1.5 rounded-full bg-white" />
                      )}
                    </span>
                    <span className="flex flex-col">
                      <span
                        className={cn(
                          'text-sm font-semibold',
                          checked ? 'text-brand-active' : 'text-fg'
                        )}
                      >
                        {opt.label}
                      </span>
                      <span className="text-xs text-fg-secondary mt-0.5">
                        {opt.description}
                      </span>
                    </span>
                  </button>
                )
              })}
            </div>
          )}
        </div>

        {/* ── Campaign picker (campaign destination only) ── */}
        {needsCampaign && (
          <div className="flex flex-col gap-2">
            <p className="text-xs font-semibold text-fg-tertiary uppercase tracking-wide">
              Select campaign
            </p>

            {isLocked ? (
              /* Locked: read-only chip, no dropdown */
              <div className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg bg-surface-sunken border border-line">
                <CampaignDot color={effectiveCampaign?.color} />
                <span className="text-sm font-medium text-fg flex-1 truncate">
                  {effectiveCampaign?.name ??
                    (isLockedCampaignError
                      ? "Couldn't load campaign name"
                      : 'this campaign')}
                </span>
              </div>
            ) : (
              <CampaignCombobox
                campaigns={campaigns?.results ?? []}
                isLoading={isCampaignsLoading}
                selected={selectedCampaign}
                onSelect={setSelectedCampaign}
              />
            )}
          </div>
        )}

        {/* ── Add tags (optional) ── */}
        {/* +8px above the tags section to set it apart from the section above. */}
        <div className="flex flex-col gap-2 mt-2">
          <p className="text-xs font-semibold text-fg-tertiary uppercase tracking-wide">
            Add tags{' '}
            <span className="normal-case font-normal text-fg-secondary">
              (optional)
            </span>
          </p>
          <TagsCombobox
            tags={contactTags}
            selected={selectedTags}
            onToggle={toggleTag}
          />
        </div>
      </div>
    </FbModal>
  )
}
